import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconUploadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconUploadOutline';
import { CobaltIconCheckmarkCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCheckmarkCircleSolid';
import { useOrganizations } from '@contexts/organizations-provider';
import { UploadApprovalLetterModal } from '@features/instructors/components/upload-approval-letter-modal/UploadApprovalLetterModal';
import { useInstructorsPendingLetterData } from '@features/instructors/contexts/pending-letter/useInstructorsPendingLetterData';
import { useApprovalLetterUpload } from '@features/instructors/hooks/use-approval-letter-upload/useApprovalLetterUpload';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { PanelLoadingState } from '@shared/components/panel-loading-state/PanelLoadingState';
import * as React from 'react';

import type { ApprovalLetterPanelContentProps } from './ApprovalLetterPanelContent.types';

import { mapPendingLetterToRows } from '../../InstructorApprovalLetterPanel.utils';
import styles from './ApprovalLetterPanelContent.module.css';

const ApprovalLetterPanelContent = React.memo(
  ({
    canUploadLetter,
  }: Readonly<ApprovalLetterPanelContentProps>): React.ReactElement => {
    const { data, isLoading, isError } = useInstructorsPendingLetterData();
    const { selectedOrganization } = useOrganizations();
    const customerId = selectedOrganization?.customerId ?? '';

    const {
      selectedTarget,
      uploadedCaseIds,
      isPending,
      uploadError,
      handleUploadClick,
      handleModalClose,
      handleFileChange,
      handleUpload,
    } = useApprovalLetterUpload({
      customerId,
    });

    const rows = React.useMemo(
      () => (data ? mapPendingLetterToRows(data.pendingApprovalLetters) : []),
      [data]
    );

    const rowsRef = React.useRef(rows);
    rowsRef.current = rows;

    const [frozenRows, setFrozenRows] = React.useState<typeof rows | null>(
      null
    );
    const freezeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

    React.useEffect(() => {
      if (uploadedCaseIds.size === 0) return;
      setFrozenRows(rowsRef.current);
      if (freezeTimerRef.current) clearTimeout(freezeTimerRef.current);
      freezeTimerRef.current = setTimeout(() => setFrozenRows(null), 3000);
    }, [uploadedCaseIds]);

    React.useEffect(
      () => () => {
        if (freezeTimerRef.current) clearTimeout(freezeTimerRef.current);
      },
      []
    );

    const displayRows = frozenRows ?? rows;

    if (isLoading) return <PanelLoadingState />;
    if (isError) return <InlineFeedback type="data-error" />;

    if (displayRows.length === 0) return <InlineFeedback type="so-empty" />;

    return (
      <>
        <div
          className={styles.approvalLetterList}
          data-testid="approval-letter-list"
        >
          {displayRows.map(row => {
            const isUploaded = uploadedCaseIds.has(row.caseId);
            return (
              <CobaltTile
                key={row.caseId}
                className={styles.approvalLetterRow}
                data-testid="approval-letter-row"
              >
                <div className={styles.tileTopRow}>
                  <div className={styles.instructorIdentity}>
                    <CobaltAvatar
                      firstName={row.firstName}
                      lastName={row.lastName}
                      color="neutral"
                    />
                    <p
                      className={`body-02 ${styles.instructorName}`}
                      data-testid="instructor-name"
                    >
                      {row.displayName}
                    </p>
                  </div>
                  {canUploadLetter &&
                    (isUploaded ? (
                      <span
                        className={`body-02 ${styles.uploadedStatus}`}
                        data-testid="uploaded-status"
                        aria-live="polite"
                      >
                        <CobaltIconCheckmarkCircleSolid aria-hidden="true" />
                        Approval letter uploaded
                      </span>
                    ) : (
                      <CobaltButton
                        variant="ghost"
                        className={styles.uploadButton}
                        data-testid="upload-letter-button"
                        aria-label={`Upload approval letter for ${row.displayName}`}
                        onClick={() => handleUploadClick(row)}
                      >
                        <CobaltIconUploadOutline aria-hidden="true" />
                        Upload
                      </CobaltButton>
                    ))}
                </div>
                <div className={styles.tileBottomRow}>
                  <div
                    className={`${styles.labeledField} ${styles.programField}`}
                  >
                    <span className={`body-01 ${styles.fieldLabel}`}>
                      Program
                    </span>
                    <span
                      className={`body-02 ${styles.fieldValue}`}
                      data-testid="program-id"
                    >
                      {row.programId}
                    </span>
                  </div>
                  <div
                    className={`${styles.labeledField} ${styles.locationField}`}
                  >
                    <span className={`body-01 ${styles.fieldLabel}`}>
                      Location
                    </span>
                    <span
                      className={`body-02 ${styles.fieldValue}`}
                      data-testid="program-location"
                    >
                      {row.location}
                    </span>
                  </div>
                </div>
              </CobaltTile>
            );
          })}
        </div>
        <UploadApprovalLetterModal
          isOpen={selectedTarget !== null}
          displayName={selectedTarget?.displayName ?? ''}
          isPending={isPending}
          uploadError={uploadError}
          onClose={handleModalClose}
          onUpload={handleUpload}
          onFileChange={handleFileChange}
        />
      </>
    );
  }
);

ApprovalLetterPanelContent.displayName = 'ApprovalLetterPanelContent';
export { ApprovalLetterPanelContent };
